import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ChildrensBookIllustrationAssetPanel from "./ChildrensBookIllustrationAssetPanel";

import {
  buildChildrensBookPhysicalPageItems,
  childrensBookWriterPageLabel,
} from "../utils/childrensBookPhysicalPagination";

export default function ChildrensBookIllustrationPlan({
  lang = "en",
  bookMap,
  illustrationPlan,
  initialUnitId = "",
  returnToWholeBookReview = false,
  assetManagementEnabled = false,
  childrensBookAssets = [],
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  childrensBookAssetActionId = "",
  onLoadChildrensBookAssetFile,
  onUploadChildrensBookIllustration,
  onRemoveChildrensBookAsset,
  onChange,
  onOpenBookMap,
  onReturn,
  onExit,
}) {
  const isFR = String(lang).toLowerCase() === "fr";
  const units = useMemo(
    () => (Array.isArray(bookMap?.units) ? bookMap.units : []),
    [bookMap?.units]
  );
  const entries = useMemo(
    () => (Array.isArray(illustrationPlan?.entries) ? illustrationPlan.entries : []),
    [illustrationPlan?.entries]
  );
  const [
    editingId,
    setEditingId,
  ] = useState(
    () => {
      const unitId =
        String(
          initialUnitId || ""
        ).trim();

      return unitId || null;
    }
  );

  const [pendingDeleteId, setPendingDeleteId] =
    useState(null);

  const initialUnitHandledRef =
    useRef("");

  useEffect(() => {
    const unitId =
      String(
        initialUnitId || ""
      ).trim();

    if (
      !unitId ||
      initialUnitHandledRef.current ===
        unitId ||
      !units.some(
        (unit) =>
          String(
            unit?.id || ""
          ).trim() === unitId
      )
    ) {
      return;
    }

    initialUnitHandledRef.current =
      unitId;

    setEditingId(
      unitId
    );

    const target =
      typeof document !== "undefined"
        ? document.getElementById(
            `illustration-unit-${encodeURIComponent(
              unitId
            )}`
          )
        : null;

    target?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  }, [
    initialUnitId,
    units,
  ]);

  const fields = [
    [
      "subject",
      isFR ? "Sujet principal" : "Illustration subject",
      isFR
        ? "Exemple : David découvre un grand éléphant près du sentier."
        : "Example: David discovers a large elephant beside the path.",
    ],
    [
      "characters",
      isFR ? "Personnages présents" : "Characters present",
      isFR
        ? "Indiquez les personnages, leur âge, leur apparence et leurs vêtements."
        : "List the characters, including age, appearance, and clothing.",
    ],
    [
      "actionMoment",
      isFR
        ? "Action ou moment représenté"
        : "Action or moment shown",
      isFR
        ? "Exemple : David recule, surpris, tandis que l’éléphant l’observe calmement."
        : "Example: David steps back in surprise while the elephant watches calmly.",
    ],
    [
      "setting",
      isFR ? "Décor et arrière-plan" : "Setting and background",
      isFR
        ? "Décrivez le lieu, l’heure, la végétation, les objets et l’arrière-plan."
        : "Describe the place, time, vegetation, objects, and background.",
    ],
    [
      "moodColour",
      isFR
        ? "Ambiance et direction des couleurs"
        : "Mood and colour direction",
      isFR
        ? "Exemple : étonnement doux, verts naturels, ciel bleu clair, sans dominante jaune."
        : "Example: gentle surprise, natural greens, pale blue sky, no strong yellow cast.",
    ],
    [
      "composition",
      isFR
        ? "Composition et point de vue"
        : "Composition and camera view",
      isFR
        ? "Exemple : vue à hauteur d’enfant, David à gauche, éléphant à droite."
        : "Example: child-height view, David on the left, elephant on the right.",
    ],
    [
      "continuityNotes",
      isFR ? "Notes de continuité" : "Continuity notes",
      isFR
        ? "Notez les vêtements, couleurs, tailles ou objets qui doivent rester identiques."
        : "Record clothing, colours, sizes, or objects that must remain consistent.",
    ],
    [
      "textSafeArea",
      isFR ? "Zone sûre pour le texte" : "Text-safe area",
      isFR
        ? "Exemple : laisser le tiers supérieur gauche calme et dégagé pour le texte."
        : "Example: keep the upper-left third calm and clear for text.",
    ],
  ];

  const statusOptions = [
    ["not_started", isFR ? "Non commencé" : "Not started"],
    ["drafting", isFR ? "Préparation en cours" : "Drafting"],
    ["ready_for_review", isFR ? "Prêt pour révision" : "Ready for review"],
    ["approved", isFR ? "Approuvé" : "Approved"],
    ["complete", isFR ? "Terminé" : "Complete"],
  ];

  function entryFor(unitId) {
    return entries.find((entry) => entry.unitId === unitId) || {
      unitId,
      subject: "",
      characters: "",
      actionMoment: "",
      setting: "",
      moodColour: "",
      composition: "",
      continuityNotes: "",
      textSafeArea: "",
      status: "not_started",
    };
  }

  function updateEntry(unitId, field, value) {
    const exists = entries.some((entry) => entry.unitId === unitId);
    onChange(
      "entries",
      exists
        ? entries.map((entry) =>
            entry.unitId === unitId ? { ...entry, [field]: value } : entry
          )
        : [...entries, { ...entryFor(unitId), [field]: value }]
    );
  }

  function requestDelete(unitId) {
    setPendingDeleteId(unitId);
  }

  function cancelDelete() {
    setPendingDeleteId(null);
  }

  function confirmDelete() {
    if (!pendingDeleteId) return;

    onChange(
      "entries",
      entries.filter(
        (entry) =>
          entry.unitId !== pendingDeleteId
      )
    );

    setEditingId(null);
    setPendingDeleteId(null);
  }

  const physicalPageItems =
    buildChildrensBookPhysicalPageItems(
      units
    );

  const started = entries.filter((entry) =>
    fields.some(
      ([field]) =>
        String(entry[field] || "").trim()
    )
  ).length;
  const complete = entries.filter((entry) => entry.status === "complete").length;

  const illustrationLabel = (value) => ({
    none: isFR ? "Aucune illustration" : "No illustration",
    small_illustration: isFR ? "Petite illustration" : "Small illustration",
    half_page: isFR ? "Illustration sur une demi-page" : "Half-page illustration",
    full_page: isFR ? "Illustration pleine page" : "Full-page illustration",
    full_spread: isFR ? "Illustration sur toute la double page" : "Full-spread illustration",
    integrated_text: isFR ? "Texte intégré à l’illustration" : "Text integrated with illustration",
  }[value] || value);

  return (
    <section aria-label={isFR ? "Plan des illustrations" : "Illustration Plan"}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#b45309" }}>
            NextScenes Book Blueprint · {isFR ? "Plan des illustrations" : "Illustration Plan"}
          </div>
          <h2 className="ns-section-title" style={{ margin: "5px 0 8px" }}>
            {isFR ? "Préparez chaque illustration" : "Plan Each Illustration"}
          </h2>
          <p className="ns-card-meta" style={{ margin: 0, maxWidth: 820 }}>
            {isFR
              ? "Transformez les besoins visuels du Plan du livre en directives claires et cohérentes."
              : "Turn Book Map visual needs into clear, consistent illustration directions."}
          </p>
        </div>
        <button className="ns-button ns-button-outline" type="button" onClick={onExit}>
          {isFR ? "Retour à Mes projets" : "Return to My Projects"}
        </button>
      </div>

      <div className="ns-nbb-card-grid ns-nbb-summary-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 }}>
        {[
          [isFR ? "Unités du livre" : "Book units", units.length],
          [isFR ? "Illustrations prévues" : "Illustrations planned", units.filter((unit) => unit.illustrationPlan !== "none").length],
          [isFR ? "Brouillons commencés" : "Drafts started", started],
          [isFR ? "Terminées" : "Complete", complete],
        ].map(([label, value]) => (
          <div key={label} className="ns-card ns-nbb-summary-card" style={{ padding: 16, borderRadius: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#64748b" }}>{label}</div>
            <div style={{ marginTop: 5, fontSize: 24, fontWeight: 900 }}>{value}</div>
          </div>
        ))}
      </div>

      {units.length === 0 ? (
        <div className="ns-card ns-nbb-major-panel" style={{ padding: 22, borderRadius: 16, textAlign: "center" }}>
          <strong>{isFR ? "Le Plan du livre est vide" : "The Book Map Is Empty"}</strong>
          <p className="ns-card-meta" style={{ margin: "8px auto 16px", maxWidth: 640 }}>
            {isFR ? "Ajoutez d’abord des pages ou doubles pages dans le Plan du livre." : "Add pages or spreads to the Book Map first."}
          </p>
          <button className="ns-button ns-button-primary" type="button" onClick={onOpenBookMap}>
            {isFR ? "Ouvrir le Plan du livre" : "Open Book Map"}
          </button>
        </div>
      ) : (
        <div className="ns-nbb-major-grid" style={{ display: "grid", gap: 16 }}>
          {units.map((unit, index) => {
            const entry = entryFor(unit.id);
            const editing = editingId === unit.id;
            const hasDraft = entries.some((item) => item.unitId === unit.id);
            const complete =
              String(entry?.status || "")
                .trim()
                .toLowerCase() === "complete";

            return (
              <article
                id={`illustration-unit-${encodeURIComponent(
                  String(unit.id || "")
                )}`}
                key={unit.id}
                className="ns-card ns-nbb-major-panel"
                style={{
                  padding: 20,
                  borderRadius: 18,
                  border:
                    "1px solid rgba(234,88,12,0.20)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <strong>
                      {childrensBookWriterPageLabel(
                        physicalPageItems[index]
                      ) || (
                        isFR
                          ? "Page du livre"
                          : "Book page"
                      )}
                      {unit.workingTitle
                        ? ` · ${unit.workingTitle}`
                        : ""}
                    </strong>
                    <p className="ns-card-meta" style={{ margin: "6px 0 0" }}>
                      {illustrationLabel(unit.illustrationPlan)}
                    </p>
                  </div>
                  <button className="ns-button ns-button-outline" type="button" onClick={() => setEditingId(editing ? null : unit.id)}>
                    {editing
                      ? (isFR ? "Fermer" : "Close")
                      : (
                          hasDraft
                            ? (
                                complete
                                  ? (isFR ? "Modifier" : "Edit")
                                  : (isFR ? "Continuer le plan" : "Continue Planning")
                              )
                            : (isFR ? "Commencer le plan" : "Start Planning")
                        )}
                  </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 16 }}>
                  {[
                    [isFR ? "Besoin d’illustration" : "Illustration requirement", illustrationLabel(unit.illustrationPlan)],
                    [isFR ? "Note visuelle héritée" : "Inherited visual note", unit.illustrationNote],
                    [isFR ? "Ce qui se passe ici" : "What happens here", unit.purpose],
                    [isFR ? "Disposition" : "Layout intention", unit.layoutIntention],
                  ].map(([label, value]) => (
                    <div key={label} className="ns-nbb-inner-panel" style={{ padding: 12, borderRadius: 12, background: "#f8fafc" }}>
                      <div style={{ fontSize: 12, fontWeight: 900, color: "#64748b" }}>{label}</div>
                      <div style={{ marginTop: 5, fontWeight: 700 }}>
                        {String(value || "").trim() || (isFR ? "Non renseigné" : "Not provided")}
                      </div>
                    </div>
                  ))}
                </div>

                <ChildrensBookIllustrationAssetPanel
                  lang={lang}
                  unit={unit}
                  assetManagementEnabled={
                    assetManagementEnabled
                  }
                  managementControlVisible={
                    !complete || editing
                  }
                  childrensBookAssets={
                    childrensBookAssets
                  }
                  childrensBookAssetsLoading={
                    childrensBookAssetsLoading
                  }
                  childrensBookAssetsError={
                    childrensBookAssetsError
                  }
                  childrensBookAssetActionId={
                    childrensBookAssetActionId
                  }
                  onLoadChildrensBookAssetFile={
                    onLoadChildrensBookAssetFile
                  }
                  onUploadChildrensBookIllustration={
                    onUploadChildrensBookIllustration
                  }
                  onRemoveChildrensBookAsset={
                    onRemoveChildrensBookAsset
                  }
                />

                {editing ? (
                  <div className="ns-nbb-editing-panel" style={{ display: "grid", gap: 16, marginTop: 18 }}>
                    {fields.map(
                      ([field, label, placeholder]) => (
                        <label
                          key={field}
                          style={{
                            display: "grid",
                            gap: 7,
                            fontWeight: 800,
                          }}
                        >
                          <span>{label}</span>

                          <textarea
                            rows={
                              field === "continuityNotes"
                                ? 4
                                : 3
                            }
                            value={entry[field]}
                            placeholder={placeholder}
                            onChange={(event) =>
                              updateEntry(
                                unit.id,
                                field,
                                event.target.value
                              )
                            }
                            style={{
                              width: "100%",
                              boxSizing: "border-box",
                              padding: 12,
                              borderRadius: 10,
                              border: "1px solid #cbd5e1",
                              font: "inherit",
                              resize: "vertical",
                            }}
                          />
                        </label>
                      )
                    )}

                    <label style={{ display: "grid", gap: 7, fontWeight: 800 }}>
                      <span>{isFR ? "État de l’illustration" : "Illustration status"}</span>
                      <select
                        value={entry.status}
                        onChange={(event) => updateEntry(unit.id, "status", event.target.value)}
                        style={{ minHeight: 44, padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff", font: "inherit" }}
                      >
                        {statusOptions.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                      {hasDraft ? (
                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          onClick={() =>
                            requestDelete(unit.id)
                          }
                          style={{
                            borderColor: "#dc2626",
                            color: "#b91c1c",
                          }}
                        >
                          {isFR ? "Supprimer le brouillon" : "Delete Draft"}
                        </button>
                      ) : null}
                      <button className="ns-button ns-button-primary" type="button" onClick={() => setEditingId(null)}>
                        {isFR ? "Terminer pour le moment" : "Done for Now"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {pendingDeleteId ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="illustration-delete-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 20,
            background: "rgba(15,23,42,0.58)",
          }}
          onClick={cancelDelete}
        >
          <div
            className="ns-card"
            style={{
              width: "min(520px, 100%)",
              padding: 24,
              borderRadius: 18,
              border:
                "1px solid rgba(220,38,38,0.28)",
              boxShadow:
                "0 24px 70px rgba(15,23,42,0.28)",
            }}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div
              style={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: 999,
                background: "#fee2e2",
                color: "#b91c1c",
                fontSize: 22,
                fontWeight: 900,
              }}
            >
              !
            </div>

            <h3
              id="illustration-delete-title"
              className="ns-card-title"
              style={{ margin: "14px 0 8px" }}
            >
              {isFR
                ? "Supprimer ce brouillon d’illustration ?"
                : "Delete This Illustration Draft?"}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.65,
              }}
            >
              {isFR
                ? "Le sujet, les personnages, l’ambiance, la composition et les autres directives de cette illustration seront supprimés. La page ou double page restera dans le Plan du livre."
                : "The subject, characters, mood, composition, and other directions for this illustration will be removed. The page or spread will remain in the Book Map."}
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 22,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={cancelDelete}
              >
                {isFR ? "Annuler" : "Cancel"}
              </button>

              <button
                className="ns-button"
                type="button"
                onClick={confirmDelete}
                style={{
                  background: "#b91c1c",
                  borderColor: "#b91c1c",
                  color: "#ffffff",
                }}
              >
                {isFR
                  ? "Supprimer le brouillon"
                  : "Delete Draft"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="ns-card" style={{ padding: 16, marginTop: 18, borderRadius: 16, background: "#fff7ed", border: "1px solid rgba(234,88,12,0.25)" }}>
        <strong>
          {isFR
            ? "Plan et ressources du projet"
            : "Plan and project assets"}
        </strong>

        <p
          className="ns-card-meta"
          style={{
            margin: "7px 0 0",
          }}
        >
          {isFR
            ? "Les directives d’illustration sont enregistrées avec le Blueprint. Les images choisies sont gérées séparément comme ressources du projet et reliées aux unités correspondantes du Plan du livre."
            : "Illustration directions are saved with the Blueprint. Chosen images are managed separately as project assets and linked to their corresponding Book Map units."}
        </p>
      </div>

      <div style={{ marginTop: 18 }}>
        <button className="ns-button ns-button-outline" type="button" onClick={onReturn}>
          {returnToWholeBookReview
            ? (
                isFR
                  ? "Retour à la révision complète du livre"
                  : "Return to Whole Book Review"
              )
            : (
                isFR
                  ? "Retour au NextScenes Book Blueprint"
                  : "Return to NextScenes Book Blueprint"
              )}
        </button>
      </div>
    </section>
  );
}
